import {
  Input,
  inject,
  Component,
  AfterViewInit,
  ViewContainerRef,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Company, CompanyFeature } from 'src/app/core/models/core.models';

import {
  EditorFormEntity,
  FormConfiguration,
  FormMode,
  FormSaveResult,
  FormTab,
} from 'src/app/core/models/form.models';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  FormsService,
  requiredNonZero,
} from 'src/app/core/services/forms.service';
import { NomLanguages, NomRoles, NomTimezones } from 'src/app/core/constants';
import {
  UserAccessLevel,
  UserService,
} from 'src/app/core/services/user.service';
import { injectManageCompanyMutation } from 'src/app/api/queries/company.queries';

import { AppState } from 'src/app/core/state/app.state';
import { AuditTrailComponent } from 'src/app/shared/components/audit-trail/audit-trail.component';
import { BreadcrumbService } from 'src/app/core/services/breadcrumb.service';
import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { ExportTemplatesComponent } from './export-templates/export-templates.component';
import { GeneralFormEditorComponent } from 'src/app/shared/components/general-form-editor/general-form-editor.component';
import { LoggerService } from '@services/logger.service';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { UserListComponent } from '../user-list/user-list.component';

import { defer, map } from 'rxjs';
import { NgClass, NgStyle, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { DynamicFormComponent } from '../../../shared/components/dynamic-form/dynamic-form.component';
import { CdkPortal } from '@angular/cdk/portal';
import { SearchInputComponent } from '../../../shared/components/search-input/search-input.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-editor-company',
  templateUrl:
    '../../../shared/components/general-form-editor/general-form-editor.component.html',
  styleUrl: './editor-company.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    NgStyle,
    forwardRef(() => DynamicFormComponent),
    FormsModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    CdkPortal,
    SearchInputComponent,
    FaIconComponent,
    LoadingComponent,
    SuiButtonComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class EditorCompanyComponent
  extends GeneralFormEditorComponent
  implements AfterViewInit
{
  @Input() company: Company;
  @Input() companyFeatures: CompanyFeature[];
  @Input() isPopup: boolean;

  editorFormEntityName = EditorFormEntity.Company;

  protected userService = inject(UserService);
  protected userApi = inject(UserApiService);
  private readonly manageCompanyMutation = injectManageCompanyMutation();

  constructor(
    protected formBuilder: FormBuilder,
    protected formService: FormsService,
    protected nomService: NomenclatureService,
    protected store: Store<AppState>,
    protected router: Router,
    protected toastService: ToastService,
    protected companyApi: CompanyApiService,
    protected logger: LoggerService,
    protected activatedRoute: ActivatedRoute,
    protected breadcrumbService: BreadcrumbService,
    protected viewContainerRef: ViewContainerRef,
    protected utilsService: UtilsService,
  ) {
    super(
      formBuilder,
      formService,
      toastService,
      store,
      logger,
      router,
      activatedRoute,
      breadcrumbService,
      viewContainerRef,
    );
  }

  ngOnInit(): void {
    const hasCompany = Boolean(this.company);
    this.formMode = hasCompany ? FormMode.Edit : FormMode.Create;
    this.logger.debug(
      hasCompany
        ? 'Loaded company:'
        : 'No company found, initializing for new company creation.',
      this.company,
    );

    super.ngOnInit();

    this.isPopupEditor = this.isPopup;

    if (hasCompany) {
      this.formService.populateFormValues(
        this.formGroup,
        this.company,
        (data) => ({
          ...data,
          company_period_start_date: this.convertTimestamp(
            data.company_period_start_date,
          ),
          company_period_end_date: this.convertTimestamp(
            data.company_period_end_date,
          ),
          // Form keys the response spells differently (flattened response key -> control name)
          city_id: data['city.id'] ?? null,
          'account_manager.id': data.account_manager_id ?? null,
          external_domain: data.has_custom_domain ? data.custom_domain : '',
          mail_server: data['mail_settings.smtp_host'] ?? '',
          mail_server_port: data['mail_settings.smtp_port'] ?? '',
          mail_server_username: data['mail_settings.smtp_username'] ?? '',
          mail_server_password: data['mail_settings.smtp_password'] ?? '',
          mail_server_security: data['mail_settings.smtp_security'] || 'none',
          mail_server_from_email: data['mail_settings.from_email'] ?? '',
          mail_server_from_name: data['mail_settings.from_name'] ?? '',
        }),
        true,
        ['name_i18n'],
      );

      this.formGroup.addControl(
        'company_id',
        this.formBuilder.control(this.company.id),
      );

      this.validateAndHandleInvalidForm();
    } else {
      // Set default values for new company
      this.formGroup.patchValue({
        default_language_id: NomLanguages.ENGLISH,
        default_time_zone_id: NomTimezones.EUROPE_SOFIA, // Europe/Sofia
        'account_status.id': 1, // Active
        'parent_company.id': '57a30f9dc46b73e9308fdeb82ed06091', // Sensika HQ
        default_password_complexity_id: 1, // Simple
        default_admin_role_id: NomRoles.DEFAULT_ADMIN_ROLE_ID, // System - Company Admin
        default_user_role_id: NomRoles.DEFAULT_USER_ROLE_ID, // System - Company Default User
      });
    }

    // * Always set up validation sync - but it only shows errors when validationEnabled is true
    this.setupValidationSync();

    if (this.companyFeatures) {
      this.buildCompanyFeatureControls(this.companyFeatures);
    }
  }

  private convertTimestamp(timestamp: number): Date | null {
    return this.utilsService.unixToDate(timestamp);
  }

  onCancel() {
    this.router.navigate(['admin', 'companies']);

    // TODO: this needs to be checked with the new Dialog
    // !this.isPopup
    //   ? this.router.navigate(['admin', 'companies'])
    //   : this.popupService.closeAllPopups();
  }

  convertToNestedStructure(flatObject: { [key: string]: any }) {
    const nestedObject: any = {
      ...flatObject,
      feed_type_id: {},
      status: {},
      max_number: {},
    };

    Object.entries(flatObject).forEach(([key, value]) => {
      const match = key.match(
        /^(feature_feed_type_id|feature_status|feature_max_number)_(\d+)$/,
      );

      if (match) {
        const [, property, featureId] = match;
        if (
          !nestedObject[property] ||
          typeof nestedObject[property] !== 'object'
        ) {
          nestedObject[property] = {};
        }

        nestedObject[property][featureId] = value;
      } else {
        // Copy other properties directly to the result object
        nestedObject[key] = value;
      }
    });

    return nestedObject;
  }

  protected override saveViaMutation(formData: any): Promise<FormSaveResult> {
    const nestedFormData = this.convertToNestedStructure({
      ...formData,
      company_period_start_date: this.utilsService.dateToYmd(
        formData.company_period_start_date,
      ),
      company_period_end_date: this.utilsService.dateToYmd(
        formData.company_period_end_date,
      ),
    });
    const settingsInSnakeCase =
      this.utilsService.convertToSnakeCase(nestedFormData);
    const isCreated = !this.company;

    return this.manageCompanyMutation
      .mutateAsync({ settingsInSnakeCase, isCreate: isCreated })
      .then((company) => ({ object: company, isCreated }));
  }

  onObjectCreated(object: any): void {
    this.router.navigate(['admin', 'companies', object.id]);
  }

  onObjectUpdated(object: any): void {
    this.router.navigate(['admin', 'companies']);
  }

  buildCompanyFeatureControls(companyFeatures: Array<CompanyFeature>) {
    const featuresPanel = []; // Panel for "Features"
    const maxNumberPanel = []; // Panel for "Max Number Of Objects"
    const objectTypesPanel = []; // Panel for "Object Types"

    const formGroupValue = {};

    companyFeatures.forEach((feature) => {
      const statusControlName = `feature_status_${feature.id}`;

      featuresPanel.push({
        propertyName: statusControlName,
        propertyLabel: feature.name,
        propertyGroup: {
          key: 'editor-company.features.available-features',
          style: 'features-list',
        },
        controlType: 'toggle',
        additionalConfig: {
          size: 'small',
        },
        formChanger: true,
        validators: [Validators.required],
        defaultValue: feature.enabled,
      });

      formGroupValue[statusControlName] = feature.enabled;

      if (feature.type === 'feed') {
        maxNumberPanel.push({
          propertyName: `feature_max_number_${feature.id}`,
          propertyLabel: feature.name,
          propertyGroup: {
            key: 'editor-company.features.current-limits-per-object-type',
            style: 'objects-limits',
          },
          controlType: 'input-number',
          validators: [Validators.required],
          defaultValue: feature.max_number ?? 0,
          additionalConfig: {
            disabled: !feature.enabled,
          },
        });

        objectTypesPanel.push({
          propertyName: `feature_feed_type_id_${feature.id}`,
          propertyLabel: feature.name,
          propertyGroup: {
            key: 'editor-company.features.current-usage-per-object-type',
            style: 'objects-usage',
          },
          controlType: 'input-number',
          defaultValue: feature.used,
          additionalConfig: { disabled: true },
        });

        formGroupValue[`feature_max_number_${feature.id}`] =
          feature.max_number ?? 0;
        formGroupValue[`feature_feed_type_id_${feature.id}`] = feature.used;
      }
    });

    // Assign the constructed panels to the formConfig structure
    const tab: FormTab = {
      tabIndex: 0,
      name: 'Features',
      label: this.translateService.instant('editor-company.label.features'),
      formPanelsClass: 'panel-features',
      icon: ['fas', 'list-alt'],
      key: 'features',

      panels: [
        {
          panelName: 'Features Panel Main',
          panelLabel: '',
          layoutConfig: { direction: 'column', gap: '0.75rem' },
          panels: [
            {
              panelName: 'Features Panel Limits',
              panelLabel: '',
              panelClass: 'features-panel',
              layoutConfig: { direction: 'row', gap: '0.75rem' },
              panels: [
                {
                  panelName: 'Features Panel List',
                  panelLabel: '',
                  panelClass: 'features-panel',
                  layoutConfig: { direction: 'column', gap: '0.75rem' },
                  items: featuresPanel,
                },
                {
                  panelName: 'Max Number Panel',
                  panelLabel: '',
                  panelClass: 'features-panel',
                  layoutConfig: { direction: 'column', gap: '0.75rem' },
                  items: maxNumberPanel,
                },
                {
                  panelName: 'Object Types Panel',
                  panelLabel: '',
                  panelClass: 'features-panel',
                  layoutConfig: { direction: 'column', gap: '0.75rem' },
                  items: objectTypesPanel,
                },
              ],
            },
          ],
        },
      ],
    };

    // Apply panel styles
    this.replaceTab('features', tab);
    this.formGroup.patchValue(formGroupValue);

    setTimeout(() => {
      companyFeatures.forEach((feature) => {
        if (feature.type !== 'feed') return;

        const maxNumberFeatureIndex = maxNumberPanel.findIndex(
          (i) => i.propertyLabel === feature.name,
        );
        const control = this.formGroup.get(`feature_status_${feature.id}`);

        if (!control || maxNumberFeatureIndex === -1) return;

        control.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((value) => {
            maxNumberPanel[maxNumberFeatureIndex].additionalConfig.disabled =
              !value;
          });
      });
    });
  }

  // Override the abstract method from the parent to provide form configuration
  loadFormConfig(): FormConfiguration {
    return {
      tabs: [
        // TAB: General Settings
        {
          label: this.translateService.instant(
            'editor-company.label.general-settings',
          ),
          key: 'general-settings',
          icon: ['fas', 'cog'],
          tabIndex: 0,
          name: '',
          panels: [
            {
              panelName: 'General Settings Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
                padding: '0px',
                border: 'none',
              },
              panels: [
                {
                  panelName: '',
                  panelLabel: '',
                  layoutConfig: { direction: 'row', gap: '0.75rem' },
                  items: [
                    {
                      propertyName: 'language_picker',
                      controlType: 'language-picker',
                      propertyGroup: {
                        key: 'editor-company.group.general-settings',
                        style: 'general-settings',
                      },
                    },
                    {
                      propertyName: 'name_i18n',
                      propertyNameLanguages: {
                        [NomLanguages.DEFAULT]: 'company_name',
                        [NomLanguages.BULGARIAN]:
                          'name_i18n.' + NomLanguages.BULGARIAN,
                        [NomLanguages.ENGLISH]:
                          'name_i18n.' + NomLanguages.ENGLISH,
                        [NomLanguages.ARABIC]:
                          'name_i18n.' + NomLanguages.ARABIC,
                      },
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.company-name',
                      ),
                      controlType: 'text',
                      propertyGroup: {
                        key: 'editor-company.group.general-settings',
                        style: 'general-settings',
                      },
                      columnIndex: 0,
                      validators: [Validators.required],
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.placeholder.company-name',
                        ),
                      },
                    },
                    {
                      propertyName: 'alias',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.company-alias',
                      ),
                      propertyGroup: {
                        key: 'editor-company.group.general-settings',
                        style: 'general-settings',
                      },

                      propertyGroupCol: 0,
                      controlType: 'text',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.placeholder.company-alias',
                        ),
                      },
                      validators: [Validators.required],
                    },
                    {
                      propertyName: 'description',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.description',
                      ),
                      controlType: 'textarea',
                      propertyGroup: {
                        key: 'editor-company.group.general-settings',
                        style: 'general-settings',
                      },
                      propertyGroupCol: 0,
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.placeholder.description',
                        ),
                        rows: 4,
                      },

                      validators: [Validators.required],
                    },

                    {
                      propertyName: 'website_url',
                      propertyGroup: {
                        key: 'editor-company.group.general-settings',
                        style: 'general-settings',
                      },
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.company-website-url',
                      ),
                      controlType: 'text',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.placeholder.company-website-url',
                        ),
                      },
                    },

                    {
                      propertyName: 'client_account_number',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.client-account-number',
                      ),
                      propertyGroup: {
                        key: 'editor-company.group.general-settings',
                        style: 'general-settings',
                      },
                      columnIndex: 0,
                      controlType: 'text',
                      validators: [requiredNonZero()],
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.placeholder.client-account-number',
                        ),
                        disabled: true,
                      },
                    },
                  ],
                },
                {
                  panelName: '',
                  panelLabel: '',
                  layoutConfig: { direction: 'column', gap: '0.75rem' },
                  items: [
                    {
                      propertyName: 'country.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.country',
                      ),
                      controlType: 'select',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.select.select-country',
                        ),
                      },
                      propertyGroup: {
                        key: 'editor-company.group.contact-information',
                        style: 'contact-information',
                      },
                      valueOptionsAsync: this.nomService.fetchCountries().pipe(
                        map((countries) => {
                          // Sort the countries array by the location name
                          countries.sort((a, b) =>
                            a.location_name.localeCompare(b.location_name),
                          );

                          // Transform the sorted countries to the appropriate format
                          let options = this.formService.transformForSelect(
                            countries,
                            (country) => country.location_id,
                            (country) => country.location_name,
                            null,
                          );

                          // Add a default empty option to the beginning of the options
                          return options;
                        }),
                      ),
                      validators: [requiredNonZero()],
                    },
                    {
                      propertyName: 'city_id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.city',
                      ),
                      controlType: 'select',
                      propertyGroup: {
                        key: 'editor-company.group.contact-information',
                        style: 'contact-information',
                      },
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.placeholder.select-city-first',
                        ),
                      },
                      valueOptionsAsync: defer(() =>
                        this.formService.getCitiesByCountryId(
                          this.formGroup,
                          'country.id',
                        ),
                      ),
                      validators: [],
                    },
                    {
                      propertyName: 'address_line1',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.address-line-1',
                      ),
                      propertyGroup: {
                        key: 'editor-company.group.contact-information',
                        style: 'contact-information',
                      },
                      controlType: 'text',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.placeholder.address-line-1',
                        ),
                      },
                    },

                    {
                      propertyName: 'address_line2',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.address-line-2',
                      ),
                      controlType: 'text',

                      propertyGroup: {
                        key: 'editor-company.group.contact-information',
                        style: 'contact-information',
                      },

                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.placeholder.address-line-2',
                        ),
                      },
                    },
                    {
                      propertyName: 'phone',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.phone-number',
                      ),
                      propertyGroup: {
                        key: 'editor-company.group.contact-information',
                        style: 'contact-information',
                      },

                      controlType: 'text',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.placeholder.phone-number',
                        ),
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },

        // TAB: Biz Admin
        {
          label: this.translateService.instant(
            'editor-company.label.biz-admin',
          ),
          key: 'biz-admin',
          userAccessLevel: UserAccessLevel.GLOBAL_ADMIN,
          icon: ['fas', 'money-bill-wave'],
          tabIndex: 1,
          name: '',
          panels: [
            {
              panelName: 'General Settings Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
                padding: '0px',
                border: 'none',
              },
              panels: [
                {
                  panelName: '',
                  panelLabel: '',
                  layoutConfig: { direction: 'row', gap: '0.75rem' },
                  items: [
                    // {
                    //   propertyName: 'status',
                    //   propertyLabel: this.translateService.instant(
                    //     'editor-company.label.status',
                    //   ),
                    //   propertyGroup: {
                    //     key: 'editor-company.group.account-classification',
                    //     style: 'account-classification',
                    //   },
                    //   propertyGroupCol: 0,
                    //   columnIndex: 1,
                    //   controlType: 'toggle',
                    //   additionalConfig: {
                    //     size: 'small',
                    //   },
                    //   defaultValue: true,
                    // },
                    {
                      propertyName: 'account_status.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.company-account-status',
                      ),
                      propertyGroup: {
                        key: 'editor-company.group.account-classification',
                        style: 'account-classification',
                      },
                      columnIndex: 0,
                      controlType: 'select',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.select.select-status',
                        ),
                      },
                      validators: [requiredNonZero()],
                      valueOptionsAsync: this.nomService
                        .fetchCompanyAccountStatuses()
                        .pipe(
                          map((types) => {
                            let options = this.formService.transformForSelect(
                              types,
                              (type) => type.id,
                              (type) => type.title,
                              null,
                            );
                            return options;
                          }),
                        ),
                    },
                    {
                      propertyName: 'account_type.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.company-account-type',
                      ),
                      propertyGroup: {
                        key: 'editor-company.group.account-classification',
                        style: 'account-classification',
                      },
                      columnIndex: 1,
                      controlType: 'select',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.select.select-type',
                        ),
                      },
                      validators: [requiredNonZero()],
                      valueOptionsAsync: this.nomService
                        .fetchCompanyAccountTypes()
                        .pipe(
                          map((types) => {
                            let options = this.formService.transformForSelect(
                              types,
                              (type) => type.id,
                              (type) => type.title,
                              null,
                            );
                            return options;
                          }),
                        ),
                    },
                    {
                      propertyName: 'relationship_type.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.company-relationship-type',
                      ),
                      propertyGroup: {
                        key: 'editor-company.group.account-classification',
                        style: 'account-classification',
                      },
                      columnIndex: 0,
                      controlType: 'select',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.select.select-relationship-type',
                        ),
                      },
                      validators: [requiredNonZero()],
                      valueOptionsAsync: this.nomService
                        .fetchCompanyRelationshipTypes()
                        .pipe(
                          map((types) => {
                            let options = this.formService.transformForSelect(
                              types,
                              (type) => type.id,
                              (type) => type.title,
                              null,
                            );
                            return options;
                          }),
                        ),
                    },
                    {
                      propertyName: 'business_entity_type.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.business-entity-type',
                      ),
                      controlType: 'select',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.select.select-entity-type',
                        ),
                      },
                      propertyGroup: {
                        key: 'editor-company.group.account-classification',
                        style: 'account-classification',
                      },
                      validators: [requiredNonZero()],
                      columnIndex: 1,
                      valueOptionsAsync: this.nomService
                        .fetchBusinessEntityTypes()
                        .pipe(
                          map((types) => {
                            let options = this.formService.transformForSelect(
                              types,
                              (type) => type.id,
                              (type) => type.title,
                              null,
                            );
                            return options;
                          }),
                        ),
                    },
                    {
                      propertyName: 'profit_center.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.company-profit-center',
                      ),
                      controlType: 'select',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.select.select-profit-center',
                        ),
                      },
                      propertyGroup: {
                        key: 'editor-company.group.account-classification',
                        style: 'account-classification',
                      },
                      validators: [requiredNonZero()],
                      columnIndex: 0,
                      valueOptionsAsync: this.nomService
                        .fetchCompanyProfitCenters()
                        .pipe(
                          map((centers) => {
                            let options = this.formService.transformForSelect(
                              centers,
                              (center) => center.id,
                              (center) => center.name,
                              null,
                            );
                            return options;
                          }),
                        ),
                    },

                    {
                      propertyName: 'business_industry.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.business-industry',
                      ),
                      controlType: 'select',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.select.select-industry',
                        ),
                        clearable: true,
                      },
                      propertyGroup: {
                        key: 'editor-company.group.account-classification',
                        style: 'account-classification',
                      },
                      validators: [],
                      columnIndex: 1,
                      valueOptionsAsync: this.nomService
                        .fetchBusinessIndustries()
                        .pipe(
                          map((types) => {
                            let options = this.formService.transformForSelect(
                              types,
                              (type) => type.id,
                              (type) => type.title,
                              null,
                            );
                            return options;
                          }),
                        ),
                    },

                    {
                      propertyName: 'business_sector.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.business-sector',
                      ),
                      controlType: 'select',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.select.select-sector',
                        ),
                        clearable: true,
                      },
                      propertyGroup: {
                        key: 'editor-company.group.account-classification',
                        style: 'account-classification',
                      },
                      validators: [],
                      columnIndex: 1,
                      valueOptionsAsync: this.nomService
                        .fetchBusinessSectors()
                        .pipe(
                          map((types) => {
                            let options = this.formService.transformForSelect(
                              types,
                              (type) => type.id,
                              (type) => type.title,
                              null,
                            );
                            return options;
                          }),
                        ),
                    },

                    {
                      propertyName: 'market.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.company-market',
                      ),
                      controlType: 'select',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.select.select-market',
                        ),
                        clearable: true,
                      },
                      propertyGroup: {
                        key: 'editor-company.group.account-classification',
                        style: 'account-classification',
                      },
                      validators: [],
                      columnIndex: 0,
                      valueOptionsAsync: this.nomService
                        .fetchCompanyMarkets()
                        .pipe(
                          map((markets) => {
                            let options = this.formService.transformForSelect(
                              markets,
                              (market) => market.id,
                              (market) => market.title,
                              null,
                            );
                            return options;
                          }),
                        ),
                    },
                    {
                      propertyName: 'legal_name',
                      propertyGroup: {
                        key: 'editor-company.group.legal-entity-information',
                        style: 'account-classification',
                      },
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.legal-entity-name',
                      ),
                      controlType: 'text',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.placeholder.legal-name',
                        ),
                      },
                    },
                    {
                      propertyName: 'notes',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.notes',
                      ),
                      controlType: 'textarea',
                      propertyGroup: {
                        key: 'editor-company.group.legal-entity-information',
                        style: 'account-classification',
                      },
                      validators: [],
                    },

                    {
                      propertyName: 'parent_company.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.parent-company',
                      ),
                      controlType: 'select',
                      propertyGroup: {
                        key: 'editor-company.group.billing-information',
                        style: 'account-billing-information',
                      },
                      validators: [requiredNonZero()],
                      columnIndex: 0,
                      valueOptionsAsync: this.userService
                        .fetchUserCompanies('', '', true, true)
                        .pipe(
                          map((companies) => {
                            let options = this.formService.transformForSelect(
                              companies,
                              (company) => company.id,
                              (company) => company.name,
                              null,
                            );
                            return options;
                          }),
                        ),
                    },
                    {
                      propertyName: 'billing_company.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.billing-company',
                      ),
                      controlType: 'select',
                      additionalConfig: {
                        clearable: true,
                      },
                      propertyGroup: {
                        key: 'editor-company.group.billing-information',
                        style: 'account-billing-information',
                      },
                      validators: [],
                      columnIndex: 1,
                      valueOptionsAsync: this.userService
                        .fetchUserCompanies('', '', true, true)
                        .pipe(
                          map((companies) => {
                            let options = this.formService.transformForSelect(
                              companies,
                              (company) => company.id,
                              (company) => company.name,
                              null,
                            );
                            return options;
                          }),
                        ),
                    },
                    {
                      propertyName: 'referral_company.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.referral-company',
                      ),
                      controlType: 'select',
                      additionalConfig: {
                        clearable: true,
                      },
                      propertyGroup: {
                        key: 'editor-company.group.billing-information',
                        style: 'account-billing-information',
                      },
                      validators: [],
                      columnIndex: 1,
                      valueOptionsAsync: this.userService
                        .fetchUserCompanies('', '', true, true)
                        .pipe(
                          map((companies) => {
                            let options = this.formService.transformForSelect(
                              companies,
                              (company) => company.id,
                              (company) => company.name,
                              null,
                            );
                            return options;
                          }),
                        ),
                    },

                    {
                      propertyName: 'account_manager.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.account-manager',
                      ),
                      controlType: 'select',
                      additionalConfig: {
                        clearable: true,
                      },
                      propertyGroup: {
                        key: 'editor-company.group.billing-information',
                        style: 'account-billing-information',
                      },
                      validators: [],
                      columnIndex: 0,
                      valueOptionsAsync: this.userApi.getAccountManagers().pipe(
                        map((companies) => {
                          let options = this.formService.transformForSelect(
                            companies,
                            (company) => company.id,
                            (company) => company.display_name,
                            null,
                          );
                          return options;
                        }),
                      ),
                    },

                    {
                      propertyName: 'entity_reg_id_type.id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.entity-registration-id-type',
                      ),
                      controlType: 'select',
                      additionalConfig: {
                        clearable: true,
                      },
                      propertyGroup: {
                        key: 'editor-company.group.billing-information',
                        style: 'account-billing-information',
                      },
                      validators: [],
                      columnIndex: 0,
                      valueOptionsAsync: this.nomService
                        .fetchEntityRegistrationIdTypes()
                        .pipe(
                          map((nomItems) => {
                            let options = this.formService.transformForSelect(
                              nomItems,
                              (nomItem) => nomItem.id,
                              (nomItem) => nomItem.title,
                              null,
                            );
                            return options;
                          }),
                        ),
                    },

                    {
                      propertyName: 'entity_reg_id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.entity-registration-id',
                      ),
                      controlType: 'text',
                      propertyGroup: {
                        key: 'editor-company.group.billing-information',
                        style: 'account-billing-information',
                      },
                      validators: [],
                      columnIndex: 1,
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.placeholder.entity-registration-id',
                        ),
                      },
                    },

                    {
                      propertyGroup: {
                        key: 'editor-company.group.billing-information',
                        style: 'account-billing-information',
                      },
                      columnIndex: 0,
                      propertyName: 'is_vat_registered',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.is-vat-registered',
                      ),
                      controlType: 'checkbox',
                      formChanger: true,
                    },

                    {
                      propertyGroup: {
                        key: 'editor-company.group.billing-information',
                        style: 'account-billing-information',
                      },
                      columnIndex: 1,
                      propertyName: 'entity_tax_id',
                      propertyLabel: this.translateService.instant(
                        'editor-company.label.vat-number',
                      ),
                      controlType: 'text',
                      additionalConfig: {
                        placeholder: this.translateService.instant(
                          'editor-company.placeholder.vat-number',
                        ),
                      },

                      shouldDisplay: (formGroup) => {
                        return formGroup.get('is_vat_registered')?.value;
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },

        // TAB: Features
        {
          label: this.translateService.instant('editor-company.label.features'),
          key: 'features',
          userAccessLevel: UserAccessLevel.GLOBAL_ADMIN,
          icon: ['fas', 'list-alt'],
          tabIndex: 0,
          name: '',
          panels: [
            {
              panelName: 'Features Panel',
              panelLabel: this.translateService.instant(
                'editor-company.label.features',
              ),
              layoutConfig: { direction: 'column', gap: '0.75rem' },
              items: [
                {
                  propertyName: 'feature_toggle',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.feature-toggle',
                  ),
                  controlType: 'toggle',
                  additionalConfig: {
                    size: 'small',
                  },
                  columnIndex: 0,
                  defaultValue: false,
                },
              ],
            },
          ],
        },

        // TAB: Data Limitations
        {
          label: this.translateService.instant(
            'editor-company.label.data-limitations',
          ),
          key: 'data-limitations',
          userAccessLevel: UserAccessLevel.GLOBAL_ADMIN,
          icon: ['fas', 'database'],
          tabIndex: 0,
          name: '',
          panels: [
            {
              panelName: 'Data Limitations Panel',
              panelLabel: '',
              layoutConfig: { direction: 'column', gap: '0.75rem' },
              items: [
                {
                  propertyName: 'company_use_relative_start_period_date',
                  controlType: 'toggle',
                  additionalConfig: {
                    size: 'small',
                  },
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.use-relative',
                  ),
                  formChanger: true,
                },
                {
                  propertyName: 'company_period_start_date_relative',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.company-period-start-date-relative-days',
                  ),
                  controlType: 'text',
                  additionalConfig: {
                    placeholder:
                      'Company Period Start Date Relative for example -30 (in days)',
                  },
                  shouldDisplay: (formGroup) => {
                    return formGroup.get(
                      'company_use_relative_start_period_date',
                    )?.value;
                  },
                },
                {
                  propertyName: 'company_period_start_date',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.company-period-start-date',
                  ),
                  controlType: 'date-picker',
                  validators: [],
                  additionalConfig: {
                    // minDate: this.utilsService.unixToDate(this.companyStartDate),
                    // maxDate: this.utilsService.unixToDate(this.companyEndDate)
                  },
                  shouldDisplay: (formGroup) => {
                    return !formGroup.get(
                      'company_use_relative_start_period_date',
                    )?.value;
                  },
                },
                {
                  propertyName: 'company_period_end_date',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.company-period-end-date',
                  ),
                  controlType: 'date-picker',
                  validators: [],
                  additionalConfig: {
                    // minDate: this.utilsService.unixToDate(this.companyStartDate),
                    // maxDate: this.utilsService.unixToDate(this.companyEndDate)
                  },
                },
              ],
            },
          ],
        },

        // TAB: Security
        {
          label: this.translateService.instant('editor-company.label.security'),
          key: 'security',
          icon: ['fas', 'shield-alt'],
          tabIndex: 0,
          name: '',
          panels: [
            {
              panelName: 'Security User Accounts Panel',
              panelLabel: this.translateService.instant(
                'editor-company.label.user-accounts',
              ),
              layoutConfig: { direction: 'column', gap: '0.75rem' },
              items: [
                {
                  propertyName: 'default_admin_role_id',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.default-admin-role',
                  ),
                  controlType: 'select',
                  additionalConfig: {
                    placeholder: this.translateService.instant(
                      'editor-company.select.select-role',
                    ),
                  },
                  propertyGroup: { key: 'security', style: 'user_accounts' },
                  validators: [requiredNonZero()],
                  // Example: reuse your existing roles endpoints if they fit
                  valueOptionsAsync: this.companyApi.getAdminRoles().pipe(
                    map((roles) =>
                      this.formService.transformForSelect(
                        roles,
                        (r) => r.id,
                        (r) => r.title,
                        null,
                      ),
                    ),
                  ),
                },
                {
                  propertyName: 'default_user_role_id',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.default-user-role',
                  ),
                  controlType: 'select',
                  additionalConfig: {
                    placeholder: this.translateService.instant(
                      'editor-company.select.select-role',
                    ),
                  },
                  propertyGroup: { key: 'security', style: 'user_accounts' },
                  validators: [requiredNonZero()],
                  valueOptionsAsync: this.companyApi.getUserRoles().pipe(
                    map((roles) =>
                      this.formService.transformForSelect(
                        roles,
                        (r) => r.id,
                        (r) => r.title,
                        null,
                      ),
                    ),
                  ),
                },
                // {
                //   propertyName: 'allowed_domains_id',
                //   propertyLabel: this.translateService.instant(
                //     'editor-company.label.allowed-domains-template',
                //   ),
                //   controlType: 'select',
                //   propertyGroup: { key: 'security', style: 'user_accounts' },
                //   validators: [],
                // },
                {
                  propertyName: 'allowed_domains',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.allowed-domains',
                  ),
                  controlType: 'textarea',
                  propertyGroup: { key: 'security', style: 'user_accounts' },
                  additionalConfig: {
                    rows: 4,
                    placeholder: this.translateService.instant(
                      'editor-company.placeholder.allowed-domains',
                    ),
                  },
                  validators: [],
                },
              ],
            },

            {
              panelName: 'Security Passwords Panel',
              panelLabel: this.translateService.instant(
                'editor-company.label.passwords',
              ),
              layoutConfig: { direction: 'column', gap: '0.75rem' },
              items: [
                {
                  propertyName: 'default_password_complexity_id',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.password-complexity',
                  ),
                  controlType: 'select',
                  additionalConfig: {
                    placeholder: this.translateService.instant(
                      'editor-company.select.select-complexity',
                    ),
                    clearable: true,
                  },
                  propertyGroup: { key: 'security', style: 'passwords' },
                  validators: [],

                  valueOptionsAsync: this.nomService
                    .fetchPasswordComplexities()

                    .pipe(
                      map((items) =>
                        this.formService.transformForSelect(
                          items,
                          (i) => i.id,
                          (i) => i.title,
                          null,
                        ),
                      ),
                    ),
                },
                {
                  propertyName: 'default_password_expiration',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.password-expiration-days',
                  ),
                  controlType: 'input-number',
                  propertyGroup: { key: 'security', style: 'passwords' },
                  validators: [],
                  additionalConfig: { min: 0 },
                },
                {
                  propertyName: 'default_password_min_length',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.password-min-length',
                  ),
                  controlType: 'input-number',
                  propertyGroup: { key: 'security', style: 'passwords' },
                  validators: [],
                  additionalConfig: { min: 1 },
                },
                {
                  propertyName: 'default_session_timeout',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.session-timeout-minutes',
                  ),
                  controlType: 'input-number',
                  propertyGroup: { key: 'security', style: 'passwords' },
                  validators: [],
                  additionalConfig: { min: 0 },
                },
              ],
            },
          ],
        },

        // TAB: Regional
        {
          label: this.translateService.instant('editor-company.label.regional'),
          key: 'regional',
          icon: ['fas', 'globe-europe'],
          tabIndex: 0, // set proper ordering if you rely on tabIndex
          name: '',
          panels: [
            {
              panelName: 'Regional General Settings Panel',
              panelLabel: this.translateService.instant(
                'editor-company.label.general-settings',
              ),
              layoutConfig: { direction: 'column', gap: '0.75rem' },
              items: [
                {
                  propertyName: 'default_language_id',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.default-language',
                  ),
                  controlType: 'select',
                  additionalConfig: {
                    placeholder: this.translateService.instant(
                      'editor-company.select.select-language',
                    ),
                  },
                  propertyGroup: { key: 'regional', style: 'general_settings' },
                  validators: [requiredNonZero()],
                  // If you have this endpoint already, wire it here:
                  valueOptionsAsync: this.nomService.fetchLanguages().pipe(
                    map((languages) =>
                      this.formService.transformForSelect(
                        languages,
                        (l) => l.id,
                        (l) => l.name,
                        null,
                      ),
                    ),
                  ),
                },
                {
                  propertyName: 'default_time_zone_id',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.default-time-zone',
                  ),
                  controlType: 'select',
                  additionalConfig: {
                    placeholder: this.translateService.instant(
                      'editor-company.select.select-time-zone',
                    ),
                  },
                  propertyGroup: { key: 'regional', style: 'general_settings' },
                  validators: [requiredNonZero()],

                  valueOptionsAsync: this.nomService.fetchTimezones().pipe(
                    map((zones) =>
                      this.formService.transformForSelect(
                        zones,
                        (z) => z.id,
                        (z) => z.name,
                        null,
                      ),
                    ),
                  ),
                },
                // {
                //   propertyName: 'default_date_time_formats',
                //   propertyLabel: this.translateService.instant('editor-company.label.default-date-time-formats'),
                //   controlType: 'textarea',
                //   propertyGroup: { key: 'regional', style: 'general_settings' },
                //   validators: [],
                //   additionalConfig: {
                //     rows: 3,
                //     placeholder: this.translateService.instant(
                //       'editor-company.placeholder.default-date-time-formats',
                //     ),
                //   },
                // },
                // {
                //   propertyName: 'default_number_formats',
                //   propertyLabel: this.translateService.instant(
                //     'editor-company.label.default-number-formats',
                //   ),
                //   controlType: 'textarea',
                //   propertyGroup: { key: 'regional', style: 'general_settings' },
                //   validators: [],
                //   additionalConfig: {
                //     rows: 3,
                //     placeholder: this.translateService.instant(
                //       'editor-company.placeholder.default-number-formats',
                //     ),
                //   },
                // },
              ],
            },
          ],
        },

        // TAB: System
        {
          label: this.translateService.instant('editor-company.label.system'),
          key: 'system',
          icon: ['fas', 'cogs'],
          tabIndex: 0,
          name: '',
          userAccessLevel: UserAccessLevel.GLOBAL_ADMIN,
          panels: [
            {
              panelName: 'System Panel',
              panelLabel: '',
              layoutConfig: { direction: 'column', gap: '0.75rem' },
              items: [
                {
                  propertyName: 'has_instagram_business_account',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.has-instagram-business-account',
                  ),
                  controlType: 'toggle',
                  propertyGroup: { key: 'system', style: 'system' },
                  additionalConfig: {
                    size: 'small',
                  },
                  defaultValue: false,
                },
              ],
            },
          ],
        },

        // TAB: UI Customization
        {
          label: this.translateService.instant(
            'editor-company.label.ui-customization',
          ),
          icon: ['fas', 'paint-brush'],
          key: 'ui-customization',
          tabIndex: 0,
          name: '',
          panels: [
            {
              panelName: 'UI Customization Panel',
              panelLabel: '',
              layoutConfig: { direction: 'column', gap: '0.75rem' },
              items: [
                {
                  propertyName: 'logo',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.logo',
                  ),
                  controlType: 'image-cropper',
                  additionalConfig: {
                    defaultImageUrl: '/assets/img/default-company-logo.png',
                    //height: 150,
                    containerStyleProperties: {
                      // width: '14.285rem',
                      // height: '10.714rem',
                    },
                  },
                },
                {
                  propertyName: 'company_chart_color_scheme',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.chart-color-scheme',
                  ),
                  controlType: 'color-palette',
                },
                {
                  propertyName: 'company_chart_heatmap_color_scheme',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.chart-heatmap-color-scheme',
                  ),
                  controlType: 'color-palette',
                },
              ],
            },
          ],
        },

        // TAB: Mail Server Settings
        {
          label: this.translateService.instant(
            'editor-company.label.mail-server-settings',
          ),
          key: 'custom-domain-mail-server',
          icon: ['fas', 'envelope'],
          tabIndex: 0,
          name: '',
          userAccessLevel: UserAccessLevel.GLOBAL_ADMIN,
          panels: [
            {
              panelName: 'Custom Domain and Mail Server',
              panelLabel: '',
              layoutConfig: { direction: 'column', gap: '0.75rem' },
              items: [
                {
                  propertyName: 'external_domain',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.external-domain',
                  ),
                  controlType: 'text',
                  columnIndex: 0,
                  defaultValue: '',
                },

                {
                  propertyName: 'mail_server',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.mail-server',
                  ),
                  controlType: 'text',

                  defaultValue: '',
                },

                {
                  propertyName: 'mail_server_port',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.mail-server-port',
                  ),
                  controlType: 'text',

                  defaultValue: '',
                },

                {
                  propertyName: 'mail_server_username',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.mail-server-username',
                  ),
                  controlType: 'text',

                  defaultValue: '',
                },

                {
                  propertyName: 'mail_server_password',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.mail-server-password',
                  ),
                  controlType: 'password',
                  defaultValue: '',
                  additionalConfig: {
                    strength: true,
                  },
                },

                {
                  propertyName: 'mail_server_security',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.mail-server-secutity',
                  ),
                  controlType: 'select',
                  valueOptions: [
                    {
                      value: 'none',
                      name: this.translateService.instant(
                        'editor-company.option.none',
                      ),
                    },
                    { value: 'ssl', name: 'SSL' },
                    { value: 'tls', name: 'TLS' },
                  ],

                  defaultValue: 'none',
                },

                {
                  propertyName: 'mail_server_from_email',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.mail-server-from-email',
                  ),
                  controlType: 'text',
                  defaultValue: '',
                },
                {
                  propertyName: 'mail_server_from_name',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.mail-server-from-name',
                  ),
                  controlType: 'text',
                  defaultValue: '',
                },
              ],
            },
          ],
        },

        // TAB: Users
        {
          label: this.translateService.instant('editor-company.label.users'),
          key: 'company-users',
          icon: ['fas', 'users'],
          tabIndex: 0,
          name: 'search-logs',
          availableInFormModes: [FormMode.Edit],
          panels: [
            {
              panelName: 'Users Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'component2',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.custom-component',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: UserListComponent,
                    inputs: {
                      companyId: this.company?.id,
                    },
                    //inputProviderAsync: this.getUserIdInputProvider.bind(this),
                    containerStyleProperties: {
                      height: 'calc(100vh - 14rem)',
                    },
                  },
                },
              ],
            },
          ],
        },

        // {
        //   label: this.translateService.instant(
        //     'editor-company.label.email-templates',
        //   ),
        //   availableInFormModes: [FormMode.Edit],
        //   key: 'email-templates',
        //   icon: ['fas', 'envelope'],
        //   panels: [
        //     {
        //       panelName: 'Email Templates',
        //       panelLabel: '',
        //       layoutConfig: {
        //         direction: 'column',
        //         gap: '0.75rem',
        //       },
        //       items: [
        //         {
        //           propertyName: 'name',
        //           propertyLabel: this.translateService.instant(
        //             'editor-company.label.name',
        //           ),
        //           controlType: 'text',
        //           validators: [Validators.required],
        //           additionalConfig: {
        //             placeholder: 'Name',
        //           },
        //         },
        //         {
        //           propertyName: 'output_format',
        //           propertyLabel: this.translateService.instant(
        //             'editor-company.label.output-format',
        //           ),
        //           controlType: 'select',
        //           formChanger: true,
        //           valueOptionsAsync: this.nomService.fetchOutputFormats().pipe(
        //             map((formats) => {
        //               const options = this.formService.transformForSelect(
        //                 formats,
        //                 (format) => format.id,
        //                 (format) => format.title,
        //               );

        //               return [...options];
        //             }),
        //           ),
        //           defaultValue: null,
        //         },
        //         {
        //           propertyName: 'language',
        //           propertyLabel: this.translateService.instant(
        //             'editor-company.label.language',
        //           ),
        //           controlType: 'select',
        //           valueOptionsAsync: this.nomService.fetchLanguages().pipe(
        //             map((languages) => {
        //               let options = this.formService.transformForSelect(
        //                 languages,
        //                 (lang) => lang.id,
        //                 (lang) => lang.name,
        //               );
        //               return [...options];
        //             }),
        //           ),
        //           defaultValue: '',
        //         },
        //         {
        //           propertyName: 'template_path',
        //           propertyLabel: this.translateService.instant(
        //             'editor-company.label.template-path',
        //           ),
        //           controlType: 'select',
        //           valueOptionsAsync: this.companyService
        //             .fetchCompanyEmailTemplates()
        //             .pipe(
        //               map((templates: string[]) =>
        //                 this.formService.transformForSelect(
        //                   templates,
        //                   (template) => template,
        //                   (template) => template,
        //                   { value: 'none', name: 'Select an option' },
        //                 ),
        //               ),
        //             ),
        //           defaultValue: 'none',
        //           shouldDisplay: (formGroup) => {
        //             return (
        //               formGroup?.get(['output_format'])?.value ==
        //               NomOutputFormat.HTML
        //             );
        //           },
        //         },
        //         {
        //           propertyName: 'file_upload',
        //           propertyLabel: this.translateService.instant(
        //             'editor-company.label.file-upload',
        //           ),
        //           controlType: 'file-upload',
        //           shouldDisplay: (formGroup) => {
        //             return (
        //               formGroup?.get(['output_format'])?.value ==
        //               NomOutputFormat.DOCX
        //             );
        //           },
        //         },
        //       ],
        //     },
        //   ],
        //   tabIndex: 0,
        //   name: '',
        // },
        // TAB: Export Templates
        {
          label: this.translateService.instant(
            'editor-company.label.export-templates',
          ),
          key: 'company-export-templates',
          icon: ['fas', 'file-export'],
          tabIndex: 0,
          name: 'export-templates',
          availableInFormModes: [FormMode.Edit],
          panels: [
            {
              panelName: 'Export Templates Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'export-templates',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.custom-component',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: ExportTemplatesComponent,
                    inputs: {
                      companyId: this.company?.id,
                    },
                    containerStyleProperties: {
                      height: 'calc(100vh - 14rem)',
                    },
                  },
                },
              ],
            },
          ],
        },
        // TAB: Audit Trail
        {
          label: this.translateService.instant(
            'editor-company.label.audit-trail',
          ),
          key: 'company-audit-trail',
          icon: ['fas', 'lock'],
          tabIndex: 0,
          name: 'audit-trail',
          availableInFormModes: [FormMode.Edit],
          panels: [
            {
              panelName: 'Audit Trail Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'audit-trail',
                  propertyLabel: this.translateService.instant(
                    'editor-company.label.custom-component',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: AuditTrailComponent,
                    inputs: {
                      object: this.company,
                      hasOwner: false,
                      timeCreatedKey: 'time_created.absolute',
                      timeModifiedKey: 'time_modified.absolute',
                    },
                    containerStyleProperties: {
                      height: 'calc(100vh - 14rem)',
                    },
                  },
                },
              ],
            },
          ],
        },
      ],
    };
  }
}
